import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { legacyPgLookupNo, type RefundFlags } from '@/features/payments/lib/refundFlags';

/**
 * QA-573 — 토스 상점관리자에서 검색할 PG 주문번호. 토스에 넘기는 orderId 는 주문 UUID 다
 * (FE providerOrderId = order.orderId, BE payment pgOrderId 도 같은 값). 별도 저장 없이 모든 토스 결제 주문(신규·과거)에 맞다.
 * - 고도몰 이관 결제: 토스 결제가 아니라 원 PG 거래번호(없으면 승인번호)를 보여 준다.
 * - 수기·자체 무통장(manualRefundOnly, 이관 제외): PG 를 거치지 않아 숨긴다.
 * - 결제를 못 읽었으면(권한·로딩) 주문 UUID 를 보여 준다(대부분 토스 결제).
 */
export const pgOrderNoOf = (
  orderId: string,
  flags: RefundFlags | null | undefined,
): { label: string; value: string } | null => {
  if (flags?.isLegacy) {
    const no = legacyPgLookupNo(flags.legacyPg);
    return no ? { label: 'PG 거래번호(고도몰)', value: no } : null;
  }
  if (flags?.manualOnly) return null;
  return { label: 'PG 주문번호', value: orderId };
};

export const OrderPgOrderNo = ({ orderId, flags }: { orderId: string; flags: RefundFlags | null | undefined }) => {
  const [copied, setCopied] = useState(false);
  const pg = pgOrderNoOf(orderId, flags);
  if (!pg) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pg.value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* 복사 실패 — 값은 화면에 그대로 있어 직접 선택할 수 있다. */
    }
  };
  return (
    <div className="mb-2 grid grid-cols-[110px_minmax(0,1fr)] items-baseline gap-2 text-sm" data-testid="order-pg-order-no">
      <span className="text-[color:var(--content-muted)]">{pg.label}</span>
      <span className="flex min-w-0 items-start gap-1">
        <span className="min-w-0 font-mono text-[13px] text-[color:var(--content-strong)] [overflow-wrap:anywhere]">{pg.value}</span>
        <button
          type="button"
          onClick={() => void copy()}
          aria-label={`${pg.label} 복사`}
          title={`${pg.label} 복사`}
          className="shrink-0 rounded p-0.5 text-[color:var(--content-muted)] hover:text-[color:var(--content-strong)]"
          data-testid="order-pg-order-no-copy"
        >
          {copied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
        </button>
      </span>
    </div>
  );
};
